import { useState, useCallback } from 'react';
import { generateClient } from 'aws-amplify/api';
import { uploadData, getUrl, remove } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';

const client = generateClient();

/**
 * Hook for managing file uploads for lectures
 */
export const useFileUpload = () => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError, setUploadError] = useState(null);

  /**
   * Upload a lecture image using the uploadCompanyImage mutation
   */
  const uploadLectureImage = useCallback(async (file) => {
    if (!file) return null;
    
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    try {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        throw new Error('Please select a valid image file (JPG, PNG, or GIF)');
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error('File size must be less than 5MB');
      }

      // Convert file to base64
      const base64String = await fileToBase64(file);
      
      // Upload using GraphQL mutation
      const response = await client.graphql({
        query: /* GraphQL */ `
          mutation UploadCompanyImage($imageBuffer: String, $imageType: String) {
            uploadCompanyImage(imageBuffer: $imageBuffer, imageType: $imageType)
          }
        `,
        variables: {
          imageBuffer: base64String,
          imageType: "lectureImage",
        },
      });
      
      // Parse response
      const result = response.data.uploadCompanyImage;
      if (result.includes("error=")) {
        throw new Error(result.replace(/{|}|error=/g, ''));
      }
      
      const fileName = result.replace(/{|}|fileName=/g, '');
      
      setUploadProgress(100);
      
      return {
        fileName,
        alt: "Lecture Image",
        file: file
      };
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Upload failed. Please try again.');
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Upload a lecture file (PDF) using Amplify Storage
   */
  const uploadLectureFile = useCallback(async (file, type = 'handout') => {
    if (!file) return null;
    
    setIsUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    
    try {
      // Validate file type for handouts (PDF only)
      if ((type === 'handout' || type.startsWith('handout_')) && file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed for handouts');
      }

      // Validate file size based on type
      const maxSize = type === 'presentation' ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
      if (file.size > maxSize) {
        throw new Error(`File size must be less than ${maxSize / 1024 / 1024}MB`);
      }

      // Upload to S3 with progress tracking
      const result = await uploadData({
        key: file.name,
        data: file,
        options: {
          accessLevel: 'protected',
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              const progress = Math.round((transferredBytes / totalBytes) * 100);
              setUploadProgress(progress);
            }
          }
        }
      }).result;
      
      // Get the identity ID for the S3 path
      const session = await fetchAuthSession();
      
      return {
        fileName: result.key,
        name: result.key,
        s3Path: `protected/${session.identityId}/${result.key}`,
        file: file
      };
      
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Upload failed. Please try again.');
      throw error;
    } finally {
      setIsUploading(false);
    }
  }, []);

  /**
   * Remove a file from S3 storage
   */
  const removeFile = useCallback(async (key, accessLevel = 'protected') => {
    if (!key) return;
    
    try {
      await remove({ 
        key, 
        options: { accessLevel } 
      });
      return true;
    } catch (error) {
      console.error('Error removing file:', error);
      throw error;
    }
  }, []);

  /**
   * Get a download URL for a file
   */
  const getFileUrl = useCallback(async (key, accessLevel = 'protected') => {
    if (!key) return null;
    
    try {
      const getUrlResult = await getUrl({
        key,
        options: { accessLevel }
      });
      return getUrlResult.url;
    } catch (error) {
      console.error('Error getting file URL:', error);
      throw error;
    }
  }, []);

  /**
   * Convert a file to base64 string
   */
  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };

  /**
   * Get the appropriate icon for a file type
   */
  const getFileIcon = useCallback((fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'ppt':
      case 'pptx':
        return 'file-powerpoint';
      case 'pdf':
        return 'file-pdf';
      case 'doc':
      case 'docx':
        return 'file-word';
      default:
        return 'file-alt';
    }
  }, []);

  /**
   * Get the appropriate color for a file type
   */
  const getFileColor = useCallback((fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();
    switch (extension) {
      case 'ppt':
      case 'pptx':
        return 'text-warning';
      case 'pdf':
        return 'text-danger';
      case 'doc':
      case 'docx':
        return 'text-primary';
      default:
        return 'text-secondary';
    }
  }, []);

  /**
   * Reset upload state
   */
  const resetUploadState = useCallback(() => {
    setIsUploading(false);
    setUploadProgress(0);
    setUploadError(null);
  }, []);

  return {
    uploadLectureImage,
    uploadLectureFile,
    removeFile,
    getFileUrl,
    getFileIcon,
    getFileColor,
    resetUploadState,
    isUploading,
    uploadProgress,
    uploadError
  };
};